import { InfoCircleOutlined } from '@ant-design/icons';
import {
  DatePicker,
  InputNumber,
  Radio,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { ASSIGN_TAG_APPLICATION_TYPE } from '../../common/constants';

const { Text } = Typography;

const INFO_TOOLTIPS = {
  selectTags:
    'Choose one or more tags to assign to every user you have selected for this bulk action.',
  applyTagTo:
    "Choose whether the tag applies for a set number of each user's upcoming orders, or for all orders placed until a specific date.",
  numberOfOrders:
    'Tag will be applied to the next X orders for this user',
  tagExpiryDate:
    'Tag will be applied to all orders placed until this date',
};

const LabelWithInfo = ({ label, tooltip, className = 'mb-8' }) => (
  <div
    className={`d-flex align-center font-size-14 ${className}`.trim()}
    style={{ gap: 6 }}
  >
    <span>{label}</span>
    <Tooltip title={tooltip} placement="topLeft" mouseEnterDelay={0.15}>
      <span
        className="d-inline-flex align-center"
        style={{ cursor: 'help', lineHeight: 0 }}
      >
        <InfoCircleOutlined
          aria-label="More information"
          style={{
            color: 'var(--ant-color-text-description, rgba(0, 0, 0, 0.45))',
            fontSize: 14,
          }}
        />
      </span>
    </Tooltip>
  </div>
);

const AssignTagDrawerContent = ({
  assignTagOptions = [],
  selectedTagIds,
  onSelectedTagIdsChange,
  assignTagApplicationType,
  onAssignTagApplicationTypeChange,
  numberOfOrders,
  onNumberOfOrdersChange,
  tagExpiryDate,
  onTagExpiryDateChange,
  numberOfOrdersError,
  tagExpiryDateError,
}) => {
  const [nonNumericError, setNonNumericError] = React.useState('');

  const getNonNumericError = (rawValue) => {
    const trimmed = String(rawValue ?? '').trim();
    if (!trimmed) return '';
    // InputNumber should be integer-only here (precision=0, step=1).
    // Treat anything that isn't digits as invalid.
    return /^\d+$/.test(trimmed)
      ? ''
      : 'Please enter a valid number.';
  };

  const handleNumberRawChange = (e) => {
    setNonNumericError(getNonNumericError(e?.target?.value));
  };

  const handleNumberKeyDown = (event) => {
    const { key } = event;

    // Allow control/navigation keys
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
      'Enter',
    ];

    if (allowedControlKeys.includes(key)) {
      return;
    }

    // Disallow non-digit characters (including minus, plus, letters, symbols, etc.)
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  };

  const resolvedError = nonNumericError || numberOfOrdersError;

  return (
    <>
    <LabelWithInfo
      label="Select tags to add"
      tooltip={INFO_TOOLTIPS.selectTags}
    />
    <div className="mb-16">
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        placeholder="Select tags"
        value={selectedTagIds}
        onChange={onSelectedTagIdsChange}
        options={assignTagOptions}
      />
    </div>

    <div className="mb-16">
      <LabelWithInfo label="Apply tag to" tooltip={INFO_TOOLTIPS.applyTagTo} />
      <Radio.Group
        className="bulk-assign-radio-group"
        value={assignTagApplicationType}
        onChange={(e) => onAssignTagApplicationTypeChange(e.target.value)}
      >
        <Radio value={ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS}>
          Number of Orders
        </Radio>
        <Radio value={ASSIGN_TAG_APPLICATION_TYPE.UNTIL_SPECIFIC_DATE}>
          Until Specific Date
        </Radio>
      </Radio.Group>
    </div>

    {assignTagApplicationType === ASSIGN_TAG_APPLICATION_TYPE.NUMBER_OF_ORDERS ? (
      <div className="mb-16">
        <LabelWithInfo
          label="Number of Orders"
          tooltip={INFO_TOOLTIPS.numberOfOrders}
          className="mb-4"
        />
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          step={1}
          precision={0}
          placeholder="Enter number of upcoming orders"
          value={numberOfOrders}
          onChange={onNumberOfOrdersChange}
          onBlur={handleNumberRawChange}
          onKeyDown={handleNumberKeyDown}
          onKeyUp={handleNumberRawChange}
          status={resolvedError ? 'error' : undefined}
        />
        {resolvedError ? (
          <Text type="danger" className="d-block mt-4">
            {resolvedError}
          </Text>
        ) : null}
      </div>
    ) : (
      <div className="mb-16">
        <LabelWithInfo
          label="Tag Expiry Date"
          tooltip={INFO_TOOLTIPS.tagExpiryDate}
          className="mb-4"
        />
        <DatePicker
          style={{ width: '100%' }}
          format="DD/MM/YYYY"
          placeholder="Select date"
          value={tagExpiryDate}
          onChange={onTagExpiryDateChange}
          status={tagExpiryDateError ? 'error' : undefined}
          disabledDate={(current) =>
            Boolean(current && current.isBefore(dayjs(), 'day'))
          }
        />
        {tagExpiryDateError ? (
          <Text type="danger" className="d-block mt-4">
            {tagExpiryDateError}
          </Text>
        ) : null}
      </div>
    )}
  </>
  );
};

export default AssignTagDrawerContent;
